import { useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useCreateMtoQualityDocMutation } from '@shared/api';
import type { QualityDocType } from '@entities/mto';
import { apiErrorMessage, QUALITY_DOC_TYPES } from '@entities/mto';
import { Button } from '@shared/ui/Button';
import { Input, Label, Select } from '@shared/ui/Input';
import { IconFile } from '@shared/ui/icons';
import {
  DocFormCard,
  FilePick,
  Form,
  FormActions,
  FormField,
  HiddenInput,
  InlineHint,
  SectionTitle,
} from '../styles';

export function AddDocForm({
  lotId,
  canWrite,
  onError,
}: {
  lotId: string;
  canWrite: boolean;
  onError: (msg: string) => void;
}) {
  const [docType, setDocType] = useState<QualityDocType>('passport');
  const [number, setNumber] = useState('');
  const [docDate, setDocDate] = useState('');
  const [issuer, setIssuer] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [createDoc, { isLoading }] = useCreateMtoQualityDocMutation();

  function reset() {
    setDocType('passport');
    setNumber('');
    setDocDate('');
    setIssuer('');
    setValidUntil('');
    setFile(null);
  }

  function pickFile(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    e.target.value = '';
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    onError('');
    const form = new FormData();
    form.append('lot_id', lotId);
    form.append('doc_type', docType);
    if (number.trim()) form.append('number', number.trim());
    if (docDate.trim()) form.append('doc_date', docDate.trim());
    if (issuer.trim()) form.append('issuer', issuer.trim());
    if (validUntil.trim()) form.append('valid_until', validUntil.trim());
    if (file) form.append('file', file, file.name);
    try {
      await createDoc({ lotId, form }).unwrap();
      reset();
    } catch (err) {
      onError(apiErrorMessage(err) ?? 'Не удалось добавить документ качества.');
      // eslint-disable-next-line no-console
      console.error('createMtoQualityDoc failed', err);
    }
  }

  const fileFieldId = `qc-doc-file-${lotId}`;

  return (
    <DocFormCard>
      <SectionTitle>Добавить документ качества</SectionTitle>
      <Form onSubmit={handleSubmit}>
        <FormField>
          <Label htmlFor={`qc-doc-type-${lotId}`}>Тип документа</Label>
          <Select
            id={`qc-doc-type-${lotId}`}
            value={docType}
            onChange={(e) => setDocType(e.target.value as QualityDocType)}
          >
            {QUALITY_DOC_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField>
          <Label htmlFor={`qc-doc-number-${lotId}`}>Номер</Label>
          <Input
            id={`qc-doc-number-${lotId}`}
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="№ документа"
          />
        </FormField>
        <FormField>
          <Label htmlFor={`qc-doc-date-${lotId}`}>Дата выдачи</Label>
          <Input
            id={`qc-doc-date-${lotId}`}
            type="date"
            value={docDate}
            onChange={(e) => setDocDate(e.target.value)}
          />
        </FormField>
        <FormField>
          <Label htmlFor={`qc-doc-issuer-${lotId}`}>Орган выдачи</Label>
          <Input
            id={`qc-doc-issuer-${lotId}`}
            value={issuer}
            onChange={(e) => setIssuer(e.target.value)}
            placeholder="Кем выдан"
          />
        </FormField>
        <FormField>
          <Label htmlFor={`qc-doc-valid-${lotId}`}>Действует до</Label>
          <Input
            id={`qc-doc-valid-${lotId}`}
            type="date"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
          />
          <InlineHint>Пусто = бессрочный</InlineHint>
        </FormField>
        <FormField>
          <Label htmlFor={fileFieldId}>Файл (любой формат)</Label>
          {/* Без accept: принимаем любой формат — PDF, скан, фото, Excel, Word. */}
          <HiddenInput id={fileFieldId} ref={fileRef} type="file" onChange={pickFile} />
          <FilePick type="button" onClick={() => fileRef.current?.click()}>
            <IconFile size={15} />
            <span>{file ? file.name : 'Приложить файл'}</span>
          </FilePick>
          <InlineHint>Любой формат: PDF, скан, фото, Excel, Word.</InlineHint>
        </FormField>
        <FormActions>
          <Button type="submit" $variant="primary" $size="sm" disabled={!canWrite || isLoading}>
            {isLoading ? 'Добавляю…' : 'Добавить'}
          </Button>
        </FormActions>
      </Form>
    </DocFormCard>
  );
}
